import { useMemo, useState } from 'react'
import Seo, { softwareAppSchema } from '../components/Seo'
import Breadcrumbs from '../components/Breadcrumbs'
import Faq from '../components/Faq'
import type { QA } from '../components/Faq'
import CalcActions from '../components/CalcActions'
import InfoTip from '../components/InfoTip'
import { Field } from '../components/Inputs'
import { computeDimensions, DEFAULT_DIMENSIONS } from '../calculators/dimensions'
import type { DimensionsInputs, LengthUnit } from '../calculators/dimensions'
import { calcsInCategory } from '../data/calculators'
import { useLocale, localizedHref } from '../i18n/locale'
import { t } from '../i18n/strings'
import { calcName, categoryName } from '../i18n/calculators'
import { fmtNum } from '../lib/format'

const PATH = '/calculators/dimensions/'
const CATEGORY = 'concrete-and-aggregate'

const TITLE = 'Dimensions Converter — Length, Area & Volume Across Units | RenoSheets'
const DESC = 'Free dimensions converter. Enter length, width and height in any common unit (in, ft, yd, mm, cm, m) to get length, area and volume in every other unit instantly. No signup.'

const FAQS: QA[] = [
  { q: 'How do I convert cubic feet to cubic yards?', a: 'Divide cubic feet by 27. One cubic yard equals 3 × 3 × 3 = 27 cubic feet, which is how concrete, gravel, mulch and topsoil are most often sold in bulk. So a 12 ft × 10 ft × 0.33 ft slab is 39.6 cu ft, or about 1.47 cubic yards of concrete.' },
  { q: 'How do I convert inches to centimeters?', a: 'Multiply inches by 2.54. So 12 in = 30.48 cm, 36 in = 91.44 cm, 48 in (a standard plywood width) = 121.92 cm. The conversion is exact — the inch was redefined in 1959 as exactly 25.4 millimeters.' },
  { q: 'How many liters in a cubic foot?', a: 'One cubic foot is approximately 28.317 liters. So a 5-gallon (US) bucket — which holds 0.668 cubic feet — is about 18.93 liters. Concrete is often specified in cubic meters: 1 cu m = 1000 liters = about 35.31 cubic feet.' },
  { q: 'How do I calculate volume in cubic yards for a concrete slab?', a: 'Multiply length × width × depth in feet, then divide by 27 to get cubic yards. A 10 × 12 ft patio at 4 in (0.333 ft) deep is 10 × 12 × 0.333 = 40 cu ft, or 1.48 cu yd. Order an extra 5–10% to cover form-board overpour and uneven sub-grade.' },
  { q: 'Why are there three input fields (length, width, height)?', a: 'So one tool covers length, area, and volume conversions at once. Enter only the length to convert that single dimension. Add a width and you get the area (length × width) in every common unit. Add a height and you also get volume (length × width × height). Set unused dimensions to 0 if you want to ignore them.' },
  { q: 'What is the difference between US and Imperial gallons?', a: 'A US gallon is 3.785 liters; an Imperial (UK / Canadian historical) gallon is 4.546 liters — about 20% larger. This converter shows US gallons, since they are the standard for paint, sealer and other product specifications in North America. Multiply US gallons by 0.833 to get Imperial gallons.' },
]

const UNITS: { v: LengthUnit; l: string }[] = [
  { v: 'in', l: 'inches (in)' },
  { v: 'ft', l: 'feet (ft)' },
  { v: 'yd', l: 'yards (yd)' },
  { v: 'mm', l: 'millimeters (mm)' },
  { v: 'cm', l: 'centimeters (cm)' },
  { v: 'm', l: 'meters (m)' },
]

function Stat({ label, value, unit }: { label: string; value: number; unit: string }) {
  return (
    <div className="rounded-md border border-border bg-surface p-2.5">
      <div className="text-[10px] font-medium uppercase tracking-wide text-text-subtle">{label}</div>
      <div className="tnum mt-0.5 text-sm font-semibold text-text">
        {fmtNum(value, value < 1 ? 4 : 3)} <span className="text-xs font-medium text-text-muted">{unit}</span>
      </div>
    </div>
  )
}

export default function DimensionsConverter() {
  const locale = useLocale()
  const L = (en: string) => localizedHref(en, locale)
  const tr = (k: string) => t(locale, k)

  const [i, setI] = useState<DimensionsInputs>(DEFAULT_DIMENSIONS)
  const r = useMemo(() => computeDimensions(i), [i])
  const set = (patch: Partial<DimensionsInputs>) => setI((p) => ({ ...p, ...patch }))

  const related = calcsInCategory(CATEGORY).filter((x) => x.slug !== 'dimensions')
  const hasArea = i.length > 0 && i.width > 0
  const hasVolume = hasArea && i.height > 0

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <Seo title={TITLE} description={DESC} path={PATH} schema={softwareAppSchema(calcName('dimensions', 'Dimensions Converter', locale), DESC, localizedHref(PATH, locale))} />
      <Breadcrumbs
        items={[
          { name: tr('crumb.home'), href: L('/') },
          { name: categoryName(CATEGORY, 'Concrete & Aggregate', locale), href: L(`/calculators/${CATEGORY}/`) },
          { name: calcName('dimensions', 'Dimensions Converter', locale), href: L(PATH) },
        ]}
      />

      <h1 className="text-3xl">Dimensions Converter</h1>
      <p className="mt-1 max-w-content text-text-muted">Enter up to three dimensions in any common construction unit. Get length, area (L × W) and volume (L × W × H) in every other unit instantly.</p>

      <div className="mt-6 grid gap-6 lg:grid-cols-5">
        <form className="rounded-xl border border-border bg-surface p-5 lg:col-span-2" onSubmit={(e) => e.preventDefault()}>
          <div className="rounded-md border border-border p-3">
            <p className="mb-1 text-sm font-medium text-text-body">
              Input unit
              <InfoTip text="The unit your length, width and height are entered in. Output appears in every other common unit automatically." />
            </p>
            <select
              value={i.unit}
              onChange={(e) => set({ unit: e.target.value as LengthUnit })}
              className="w-full rounded-md border border-border bg-surface px-3 py-2.5 text-sm text-text focus:border-brand focus:outline-none"
            >
              {UNITS.map((u) => (
                <option key={u.v} value={u.v}>{u.l}</option>
              ))}
            </select>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3">
            <Field label={`Length (${i.unit})`} value={i.length} onChange={(n) => set({ length: n })} suffix={i.unit} step={i.unit === 'mm' ? 1 : 0.1} />
            <Field label={`Width (${i.unit})`} value={i.width} onChange={(n) => set({ width: n })} suffix={i.unit} step={i.unit === 'mm' ? 1 : 0.1} />
            <Field label={`Height / depth (${i.unit})`} value={i.height} onChange={(n) => set({ height: n })} suffix={i.unit} step={i.unit === 'mm' ? 1 : 0.1} />
          </div>

          <p className="mt-3 text-xs text-text-subtle">Tip: leave width at 0 for a length-only conversion, or leave height at 0 for area only (no volume).</p>

          <CalcActions locale={locale} className="mt-5 border-t border-border pt-4" />
        </form>

        <section className="rounded-xl border border-border bg-surface p-5 lg:col-span-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-text-muted">Length ({i.length} {i.unit})</h2>
          <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Stat label="Inches" value={r.lengthIn} unit="in" />
            <Stat label="Feet" value={r.lengthFt} unit="ft" />
            <Stat label="Centimeters" value={r.lengthCm} unit="cm" />
            <Stat label="Meters" value={r.lengthM} unit="m" />
          </div>

          <h2 className="mt-6 text-sm font-semibold uppercase tracking-wide text-text-muted">
            Area (L × W)
            {!hasArea && <span className="ml-2 text-[10px] font-normal text-text-subtle">— enter width to enable</span>}
          </h2>
          <div className={`mt-2 grid grid-cols-2 gap-2 sm:grid-cols-5 ${hasArea ? '' : 'opacity-40'}`}>
            <Stat label="Sq inches" value={r.areaSqIn} unit="in²" />
            <Stat label="Sq feet" value={r.areaSqFt} unit="ft²" />
            <Stat label="Sq yards" value={r.areaSqYd} unit="yd²" />
            <Stat label="Sq cm" value={r.areaSqCm} unit="cm²" />
            <Stat label="Sq meters" value={r.areaSqM} unit="m²" />
          </div>

          <h2 className="mt-6 text-sm font-semibold uppercase tracking-wide text-text-muted">
            Volume (L × W × H)
            {!hasVolume && <span className="ml-2 text-[10px] font-normal text-text-subtle">— enter height to enable</span>}
          </h2>
          <div className={`mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3 ${hasVolume ? '' : 'opacity-40'}`}>
            <Stat label="Cubic inches" value={r.volCuIn} unit="in³" />
            <Stat label="Cubic feet" value={r.volCuFt} unit="ft³" />
            <Stat label="Cubic yards" value={r.volCuYd} unit="yd³" />
            <Stat label="Cubic meters" value={r.volCuM} unit="m³" />
            <Stat label="Liters" value={r.volLiters} unit="L" />
            <Stat label="US gallons" value={r.volUsGallons} unit="gal" />
          </div>

          <p className="mt-6 text-[11px] text-text-subtle">Conversions use exact SI factors (1 in = 25.4 mm exactly). Rounded for display only — math is full precision.</p>
        </section>
      </div>

      <article className="mx-auto mt-8 max-w-content text-text-body">
        <h2 className="text-2xl">{locale === 'fr' ? 'Pourquoi la précision de conversion compte en construction' : 'Why conversion precision actually matters on a job site'}</h2>
        <p className="mt-3">
          {locale === 'fr'
            ? 'De petites différences d’arrondi s’accumulent vite dans les calculs de construction. Convertir une seule mesure du métrique à l’impérial et arrondir au 1/8 de pouce près introduit une petite erreur, mais si ce chiffre arrondi est ensuite multiplié — pour une surface, un volume ou une liste de coupe — l’erreur s’accumule avec lui. Un mur mesuré à 3,65 m et arrondi à 12 pi (plutôt que le 11,98 pi plus précis) est décalé d’environ un pouce et demi une fois converti, assez petit pour passer inaperçu mais assez grand pour fausser une liste de coupe serrée ou une disposition de montants. Les plans de construction et les matériaux vendus en Amérique du Nord sont encore massivement en impérial (pouces, pieds), tandis que les plans d’architectes formés en Europe ou les matériaux importés sont souvent en métrique — mélanger les deux sans convertir soigneusement est l’une des sources d’erreurs de mesure les plus courantes sur un chantier. Quand la précision compte, convertissez une seule fois à partir de la mesure d’origine plutôt que de convertir un chiffre déjà arrondi une seconde fois, car chaque étape d’arrondi ajoute sa propre petite erreur.'
            : 'Small rounding differences compound fast in construction math. Converting a single measurement from metric to imperial and rounding to the nearest 1/8 inch introduces a tiny error, but if that rounded number gets multiplied — for area, volume, or a cut list — the error scales with it. A wall measured as 3.65 m and rounded to 12 ft (rather than the more precise 11.98 ft) is off by about an inch and a half once converted — small enough to miss but large enough to throw off a tight cut list or a stud layout. Construction drawings and building supplies in North America are still overwhelmingly imperial (inches, feet), while plans from European-trained architects or imported materials are often metric — mixing the two without converting carefully is one of the most common sources of measurement errors on a job site. When precision matters, convert once from the original source measurement rather than converting an already-rounded number a second time, since each rounding step adds its own small error.'}
        </p>
        <h2 className="mt-8 text-2xl">How dimension conversion works</h2>
        <p className="mt-3">Every length unit converts to every other through the meter, which is the SI base unit. The exact conversion factors that drive this calculator:</p>
        <pre className="mt-3 overflow-x-auto rounded-lg bg-surface-2 p-3 text-sm text-text">{`1 in   = 0.0254 m       (exact, since 1959)
1 ft   = 0.3048 m       (exact, 12 × 0.0254)
1 yd   = 0.9144 m       (exact, 36 × 0.0254)
1 mm   = 0.001 m
1 cm   = 0.01 m

area   = L × W          (then convert each axis squared)
volume = L × W × H      (then convert each axis cubed)
1 L    = 0.001 m³
1 US gal = 3.785411784 L (exact, since 1893)
1 cu yd  = 27 cu ft     (the buy unit for bulk concrete and aggregate)`}</pre>

        <h3 className="mt-6 text-xl">Common construction conversions to memorize</h3>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li><strong>27 cu ft = 1 cu yd</strong> — the bulk-material buy unit for concrete, gravel, mulch and topsoil.</li>
          <li><strong>2.54 cm = 1 in</strong> — exactly. The whole imperial-to-metric system pivots on this number.</li>
          <li><strong>9 sq ft = 1 sq yd</strong> — relevant for fabric, carpet and small lawn-care orders.</li>
          <li><strong>3.785 L = 1 US gallon</strong> — paint, stain, sealer, primer all priced per US gallon in North America.</li>
          <li><strong>1 cu ft ≈ 7.48 US gallons</strong> — useful for sizing rain barrels, pond volumes and concrete forms.</li>
        </ul>

        <h3 className="mt-6 text-xl">Common mistakes</h3>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li><strong>Confusing US and Imperial gallons.</strong> Imperial gallons are about 20% larger than US gallons. This calculator outputs US gallons, the North American default.</li>
          <li><strong>Forgetting to cube the conversion factor for volume.</strong> 1 m = 3.281 ft, but 1 m³ = 35.314 ft³ (not 3.281). Always cube the linear factor for volumes.</li>
          <li><strong>Mixing dimensions and units.</strong> If your length is in feet, your width must also be in feet — this calculator forces a single input unit so you cannot accidentally mix them.</li>
          <li><strong>Reading "yards" as "feet" on a delivery slip.</strong> 5 cubic yards of gravel is 5 × 27 = 135 cubic feet — quite different from 5 cubic feet.</li>
        </ul>
      </article>

      <Faq items={FAQS} />

      <section className="mt-10">
        <h2 className="text-2xl">Related calculators</h2>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {related.map((x) => (
            <a
              key={x.slug}
              href={L(`/calculators/${x.slug}/`)}
              className={`rounded-lg border p-3 text-sm transition-colors ${
                x.live
                  ? 'border-border bg-surface-2 font-medium text-brand-text hover:border-brand hover:bg-surface'
                  : 'border-dashed border-border bg-surface-2 text-text-muted'
              }`}
            >
              {calcName(x.slug, x.name, locale)} {x.live ? '' : <span className="text-text-subtle">{tr('calc.soon')}</span>}
            </a>
          ))}
        </div>
      </section>

      <div className="mx-auto mt-8 max-w-content border-t border-border pt-4 text-xs text-text-subtle">
        Reviewed by the RenoSheets team for calculation accuracy. Method: all conversions via meters using exact SI factors (1 in = 25.4 mm, 1 ft = 0.3048 m, 1 US gal = 3.785411784 L). Last updated 2026-06-02. Estimates rounded for display; underlying math is full double-precision.
      </div>
    </div>
  )
}
